import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import AddIcon from '@mui/icons-material/AddOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { PERMISSIONS } from '@/types/auth';
import CustomerFormDialog from './CustomerFormDialog';
import type { CustomerFormValues } from './CustomerFormDialog';
import type { CreateCustomerRequest, CustomerDto, UpdateCustomerRequest } from '@/types/materials';

export default function CustomerMaster() {
  const { t } = useTranslation('customerMaster');
  const queryClient = useQueryClient();

  const { data: customers = [], isLoading } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => (await apiClient.get<CustomerDto[]>('/customers')).data,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<CustomerDto | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<CustomerDto | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['customers'] });

  const saveMutation = useMutation({
    mutationFn: (values: CustomerFormValues) => {
      if (editingCustomer) {
        const payload: UpdateCustomerRequest = values;
        return apiClient.put(`/customers/${editingCustomer.id}`, payload);
      }
      const payload: CreateCustomerRequest = values;
      return apiClient.post('/customers', payload);
    },
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      setEditingCustomer(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/customers/${id}`),
    onSuccess: () => {
      invalidate();
      setConfirmDelete(null);
    },
  });

  const columns: DataTableColumn<CustomerDto>[] = [
    { field: 'code', headerName: t('table.code') },
    { field: 'name', headerName: t('table.name') },
    { field: 'gstin', headerName: t('table.gstin'), render: (r) => r.gstin ?? '—' },
    { field: 'contactPerson', headerName: t('table.contactPerson'), render: (r) => r.contactPerson ?? '—' },
    { field: 'phone', headerName: t('table.phone'), render: (r) => r.phone ?? '—' },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) => (
        <IfPermitted permission={PERMISSIONS.CUSTOMER_MANAGE}>
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Button
              size="small"
              onClick={() => {
                setEditingCustomer(r);
                setDialogOpen(true);
              }}
            >
              {t('edit')}
            </Button>
            <Button size="small" color="error" onClick={() => setConfirmDelete(r)}>
              {t('delete')}
            </Button>
          </Stack>
        </IfPermitted>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.CUSTOMER_MANAGE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingCustomer(null);
                setDialogOpen(true);
              }}
            >
              {t('addCustomer')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={customers} loading={isLoading} searchable searchPlaceholder={t('search')} getRowId={(r) => r.id} exportFileName="customers" />

      <CustomerFormDialog
        open={dialogOpen}
        customer={editingCustomer}
        onClose={() => {
          setDialogOpen(false);
          setEditingCustomer(null);
        }}
        onSubmit={(values) => saveMutation.mutate(values)}
        loading={saveMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        message={t('deleteCustomer.message')}
        loading={deleteMutation.isPending}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
        onClose={() => setConfirmDelete(null)}
      />
    </>
  );
}
